<template>
    <section>
        <b-row style="max-width: 1440px;">
            <b-col :md="5" class="mb-2">
                <slot name="groupAndAccounts" :loading="loading"></slot>
            </b-col>

            <b-col :md="4" class="mb-2">
                <CostPivotSlider
                    :loading="loading"
                    :theme="theme"
                    :themeColors="themeColors"
                    :minDate="costPivotSummary.minDate"
                    :maxDate="costPivotSummary.maxDate"
                    :indexes="selectedMonthRange"
                    :data="costPivotSummary"
                    :downloadMode="downloadMode"
                    @change:indexes="changeIndexes"
                ></CostPivotSlider>
            </b-col>

            <b-col :md="3" class="mb-2 pr-0" v-if="costPivotSummary.indexes && costPivotSummary.indexes.length > 0">
                <CostPivotOptionV1
                    v-model="fieldStatus"
                    :loading="loading"
                    :items="items"
                    :filters="costPivotFilterNames"
                    :availableExport="!!costPivotData.length && statusCode.data === 200"
                    :downloadMode="downloadMode"
                    @create:filter="createFilter"
                    @delete:filter="deleteFilter"
                    @export:csv="exportCsv"
                ></CostPivotOptionV1>
            </b-col>
        </b-row>
        <b-row v-if="!downloadMode && (!costPivotSummary.indexes || costPivotSummary.indexes.length === 0)">
            <b-col :md="12" class="mb-2">
                <b-alert
                    v-if="!loading"
                    variant="warning"
                    show
                    class="text-center p-2"
                >
                    <div class="alert-body">
                        {{ $t('org.message.There are no usage details') }}
                    </div>
                </b-alert>
            </b-col>
        </b-row>
        <!-- EXCEPTION 상태 에러 메시지 표시 -->
        <b-row v-if="downloadMode && (costDownloadStatus === 'EXCEPTION' || costPivotDownloadStatus === 'EXCEPTION') && serverMessage" style="max-width: 1440px;">
            <b-col md="12" class="pr-0">
                <b-alert
                    variant="danger"
                    show
                    dismissible
                    @dismissed="serverMessage = null"
                    class="mb-1"
                >
                    <div class="alert-body">
                        <feather-icon icon="AlertCircleIcon" class="mr-50" />
                        {{ serverMessage }}
                    </div>
                </b-alert>
            </b-col>
        </b-row>
        <!-- 저장된 필터와 다운로드 모드 통합 로우 -->
        <b-row v-if="costPivotSummary.indexes && costPivotSummary.indexes.length > 0" style="max-width: 1440px;">
            <b-col md="12" class="pr-0">
                <div class="d-flex justify-content-between align-items-center mb-50 mr-0">
                    <!-- 저장된 필터 (왼쪽) -->
                    <div v-if="!downloadMode && costPivotFilterNames.length">
                        <b-dropdown
                            left
                            size="sm"
                            :variant="selectedFilterIdx ? 'primary' : 'secondary'"
                            :disabled="loading"
                            noFlip
                        >
                            <template #button-content>
                                <template v-if="selectedFilterIdx">
                                    <i class="fas fa-check mr-25" /> {{ costPivotFilterNames.find(item => item.costPivotFilterIdx == selectedFilterIdx).name }}
                                </template>
                                <template v-else>
                                    {{ $t('v4.Saved Filters')}} ({{costPivotFilterNames.length}})
                                </template>
                            </template>
                            <b-dropdown-item
                                v-for="(item, index) in costPivotFilterNames"
                                :key="index"
                                :variant="selectedFilterIdx === item.costPivotFilterIdx ? 'primary' : 'outline-secondary'"
                                style="cursor:pointer;"
                                @click="fetchCostPivotFilter(item)"
                            >{{ item.name }}</b-dropdown-item>
                        </b-dropdown>
                    </div>
                    <!-- 빈 div로 왼쪽 공간 차지 (저장된 필터가 없을 때) -->
                    <div v-else></div>

                    <!-- 다운로드 모드 스위치 (오른쪽 끝) -->
                    <div v-if="!internalUser" class="d-flex align-items-center">
                        <b-badge
                            variant="warning"
                            pill
                            class="mr-50"
                        >
                            new
                        </b-badge>
                        <b-form-checkbox
                            :checked="downloadMode"
                            switch
                            inline
                            class="mb-0 mr-0"
                            :disabled="loading"
                            @change="onDownloadModeChange"
                        >
                            <span class="switch-icon-left">
                                <feather-icon
                                    icon="CheckIcon"/>
                            </span>
                            <span class="font-weight-bold">{{ $t('v4["Download Mode"]') }}</span>
                            <feather-icon
                                id="download-mode-tooltip"
                                icon="HelpCircleIcon"
                                size="14"
                                class="ml-25 text-muted"
                            />
                        </b-form-checkbox>
                        <b-tooltip
                            target="download-mode-tooltip"
                            triggers="hover"
                            placement="left"
                        >
                            {{ $t('v4["Enable to prepare data for download without API calls"]') }}
                        </b-tooltip>
                    </div>
                </div>
            </b-col>
        </b-row>
        <b-row v-if="(costPivotSummary.indexes && costPivotSummary.indexes.length > 0) || downloadMode" style="max-width: 1440px;">
            <b-col md="12" class="pr-0">
                <b-card
                    no-body
                    class="pb-0"
                >
                    <CostPivotTable
                        ref="pivotTable"
                        v-model="items"
                        :items="items"
                        :loading="loading"
                        :data="downloadMode ? [] : costPivotData"
                        :size="downloadMode ? 0 : costPivotSummary.sizeOfData"
                        :pivotFilter="pivotFilter"
                        :fieldStatus="fieldStatus"
                        :accountMap="accountMap"
                        :serverMessage="serverMessage"
                        :internalUser="internalUser"
                        :statusCode="downloadMode ? (downloadModeReady ? (downloadModeNoData ? 204 : 205) : null) : statusCode.data"
                        :downloadStatus="downloadMode ? costDownloadStatus : costPivotDownloadStatus"
                        :downloadLink="downloadMode ? costDownloadLink : costPivotDownloadLink"
                        :downloadMode="downloadMode"
                        @sync:compositSource="updateCompositSources"
                        @sync:items="syncFilter"
                        @sync:filter="syncFilter"
                        @fetch:filterNames="fetchFilterNames"
                        @fetch:pivotData="fetchPivotData"
                        @download:largeFile="fetchCostPivotDownload"
                        @update:pivotFilter="updatePivotFilter"
                        @reload:pivotFilter="fetchPivotDataWithoutParams"
                    >
                        <template slot="dropdown-description">
                            <div
                                v-if="costPivotSummary.indexes"
                                class="d-inline-block mb-50"
                            >
                                <b-form-checkbox
                                    v-model="fieldStatus.description"
                                    switch
                                    inline
                                    :disabled="loading"
                                    button
                                    size="sm"
                                    class="mr-50"
                                >
                                    {{ $t('v4.Description Items') }} <i class="fas" :class="{'fa-angle-down': !fieldStatus.description, 'fa-angle-up': fieldStatus.description}" />
                                </b-form-checkbox>
                            </div>
                        </template>
                        <template slot="dropdown-resource">
                            <div
                                v-if="costPivotSummary.indexes"
                                class="d-inline-block mb-50"
                                :class="{ 'd-block': fieldStatus.description || fieldStatus.resource }"
                            >
                                <b-form-checkbox
                                    v-model="fieldStatus.resource"
                                    switch
                                    inline
                                    :disabled="loading"
                                    button
                                    size="sm"
                                >
                                    {{ $t('v4.Resource & Tags') }} <i class="fas" :class="{'fa-angle-down': !fieldStatus.resource, 'fa-angle-up': fieldStatus.resource}" />
                                </b-form-checkbox>
                            </div>
                        </template>
                    </CostPivotTable>
                </b-card>
            </b-col>
        </b-row>

        <!-- 필터 추가 모달 -->
        <b-modal
            v-model="showAddFilterModal"
            :title="$t('v4.Save Filter')"
            size="md"
            @ok="handleAddFilter"
            @hidden="filterName = ''"
        >
            <b-form-group :label="$t('v4.Filter Name')">
                <b-form-input
                    v-model="filterName"
                    :placeholder="$t('v4.Enter name')"
                />
            </b-form-group>
            <p class="text-muted mt-25 mb-0">
                <small>- {{$t('org.billing.Saves current items and filtering values')}}</small>
            </p>
            <p class="text-muted mt-0 mb-0">
                <small>- {{$t('org.billing.For AWS Account / Group, items are saved but filters are not')}}</small>
            </p>
        </b-modal>

        <!-- 필터 삭제 모달 -->
        <b-modal
            v-model="showDeleteFilterModal"
            :title="$t('v4.Delete Filters')"
            size="md"
            @ok="handleDeleteFilter"
            @hidden="selectedFiltersToDelete = []"
        >
            <b-form-group :label="$t('v4.Select filters you want to delete')">
                <b-form-checkbox-group
                    v-model="selectedFiltersToDelete"
                    :options="filterOptions"
                    stacked
                />
            </b-form-group>
        </b-modal>
    </section>
</template>

<script>
import { getCurrentInstance, computed, inject, toRefs, ref, watch, onMounted } from '@vue/composition-api'
import { RESET_DOWNLOAD_LINK, RESET_DOWNLOAD_STATUS } from './_store/mutation-type'
import CostPivotTable from './_components/CostPivotTable'
import CostPivotSlider from './_components/CostPivotSlider'
import CostPivotOptionV1 from './_components/CostPivotOptionV1'
import useCostPivot from '@/composables/useCostPivot'
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import useSelectMonthRange from '@/composables/useSelectMonthRange'
import bus from '@/libs/bus'

export default {
    components: {
        CostPivotTable,
        CostPivotSlider,
        CostPivotOptionV1
    },
    props: {
        userIdx: {
            type: Number
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { userIdx } = toRefs(props)
        const loading = ref(false)
        const serverMessage = ref(null)
        const selectedFilterIdx = ref(null)
        const showAddFilterModal = ref(false)
        const showDeleteFilterModal = ref(false)
        const filterName = ref('')
        const selectedFiltersToDelete = ref([])
        const isModeSwitching = ref(false) // 모드 전환 중 플래그
        const isProjectChanging = ref(false) // 프로젝트/그룹 변경 중 플래그
        const themeColors = inject('themeColors')
        const theme = inject('theme')
        const {
            // selectMonthRange,
            selectedMonthRange
        } = useSelectMonthRange('$_costPivot')
        const { getCompositSources } = useCostPivot()
        const {
            qtypeParam
        } = useSelectProjectAndGroup()
        const items = ref({
            rows: ['usageAccountId'],
            cols: ['ym'],
            compositSources: ['usageAccountId', 'ym'],
            value: 'unblendedCost'
        })
        const fieldStatus = ref({
            description: false,
            resource: false
        })
        const accountMap = computed(() => store.getters.accountMap)
        const costPivotData = computed(() => store.getters['$_costPivot/costPivotData'])
        const costPivotSummary = computed(() => store.getters['$_costPivot/costPivotSummary'])
        const costPivotFilterNames = computed(() => store.getters['$_costPivot/costPivotFilterNames'])
        const costPivotFilterContent = computed(() => store.getters['$_costPivot/costPivotFilterContent'])
        const costPivotFilterContentValue = computed(() => costPivotFilterContent.value.value || 'unblendedCost')
        const costPivotSavedFilter = computed(() => store.getters['$_costPivot/costPivotSavedFilter'])
        const costPivotDownloadStatus = computed(() => store.getters['$_costPivot/costPivotDownloadStatus'])
        const costPivotDownloadLink = computed(() => store.getters['$_costPivot/costPivotDownloadLink'])
        const statusCode = computed(() => store.getters['$_costPivot/costPivotStatus'])
        const internalUser = computed(() => store.getters.internalUser)
        const changeProject = computed(() => store.getters.changeProject)
        const downloadMode = computed(() => store.getters['$_costPivot/downloadMode'])
        const costDownloadLink = computed(() => store.getters['$_costPivot/costDownloadLink'])
        const costDownloadStatus = computed(() => store.getters['$_costPivot/costDownloadStatus'])
        const downloadModeNoData = ref(false) // 다운로드 모드에서 204 상태 추적
        const downloadModeReady = ref(false) // 다운로드 모드 API 응답 완료 여부

        // 페이지 진입 시 항상 일반 모드로 초기화
        onMounted(() => {
            // 다운로드 모드가 켜져있으면 끄기
            if (downloadMode.value) {
                store.dispatch('$_costPivot/setDownloadMode', false)
            }
        })

        const onDownloadModeChange = async (value) => {
            // 모드 전환 시작
            isModeSwitching.value = true

            // 먼저 스토어 상태 업데이트
            store.dispatch('$_costPivot/setDownloadMode', value)

            if (value) {
                // 일반 -> 다운로드 모드 전환
                // 필터와 필드 상태를 초기 상태로 복원
                pivotFilter.value = {}
                items.value = {
                    rows: ['usageAccountId'],
                    cols: ['ym'],
                    compositSources: ['usageAccountId', 'ym'],
                    value: 'unblendedCost'
                }

                // 다운로드 상태 초기화
                store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')

                // 다운로드 모드용 period API 호출
                downloadModeReady.value = false
                store.dispatch('$_costPivot/getCostDownloadPeriod', {
                    qtype: qtypeParam.value.qtype,
                    projectIdx: qtypeParam.value.projectIdx,
                    groupIdx: qtypeParam.value.groupIdx
                }).then(response => {
                    // 204 응답인지 확인
                    if (response.status === 204 || !response.summary || Object.keys(response.summary).length === 0) {
                        downloadModeNoData.value = true
                    } else {
                        downloadModeNoData.value = false
                    }
                    downloadModeReady.value = true

                    const periodData = response.summary
                    if (periodData && periodData.minDate && periodData.maxDate) {
                        // summary 업데이트
                        store.commit('$_costPivot/UPDATE_COST_PIVOT_SUMMARY', {
                            minDate: periodData.minDate,
                            maxDate: periodData.maxDate,
                            indexes: periodData.indexes,
                            sizeOfData: periodData.sizeOfData
                        })

                        // 슬라이더 초기값 설정
                        if (periodData.indexes && periodData.indexes.length > 0) {
                            // 최근 3개월 선택 (또는 전체 기간이 3개월 미만이면 전체 선택)
                            const defaultIndexes = periodData.indexes.length <= 3
                                ? periodData.indexes
                                : periodData.indexes.slice(-3)
                            store.commit('$_costPivot/FETCH_SELECTED_MONTH_RANGE', defaultIndexes)
                        } else {
                            store.commit('$_costPivot/FETCH_SELECTED_MONTH_RANGE', [])
                        }
                    }
                    // 모드 전환 완료
                    isModeSwitching.value = false
                }).catch(error => {
                    console.error('Failed to fetch download period:', error)
                    isModeSwitching.value = false
                })
            } else {
                // 다운로드 -> 일반 모드 전환
                // 플래그 초기화
                downloadModeNoData.value = false
                downloadModeReady.value = false
                // 필드와 필터를 초기 상태로 복원
                pivotFilter.value = {}
                items.value = {
                    rows: ['usageAccountId'],
                    cols: ['ym'],
                    compositSources: ['usageAccountId', 'ym'],
                    value: 'unblendedCost'
                }

                // 먼저 기본 인덱스 계산
                let defaultIndexes = []
                if (costPivotSummary.value.indexes && costPivotSummary.value.indexes.length > 0) {
                    defaultIndexes = costPivotSummary.value.indexes.length <= 3
                        ? costPivotSummary.value.indexes
                        : costPivotSummary.value.indexes.slice(-3)
                    // 슬라이더 값 먼저 설정 (watcher 트리거 방지)
                    store.commit('$_costPivot/FETCH_SELECTED_MONTH_RANGE', defaultIndexes)
                }

                // 기본 인덱스로 한 번만 API 호출
                fetchPivotData({
                    ...requestOption.value,
                    indexes: defaultIndexes.length ? defaultIndexes : []
                }).then(() => {
                    // 모드 전환 완료
                    isModeSwitching.value = false
                }).catch(error => {
                    console.error('Failed to fetch data:', error)
                    isModeSwitching.value = false
                })

                store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')
            }
        }
        const pivotFilter = ref({})
        const dataToken = computed(() => store.getters['$_costPivot/costPivotDataToken'])
        const syncFilter = (filter) => {
            store.commit('$_costPivot/FETCH_COST_PIVOT_FILTER_CONTENT', filter)
        }
        const requestOption = computed(() => ({
            ...qtypeParam.value,
            indexes: selectedMonthRange.value,
            compositSources: getCompositSources(items.value.rows, items.value.cols),
            value: items.value.value,
            pivotFilter: {
                ...pivotFilter.value,
                usageAccountId: pivotFilter.value?.usageAccountId?.map(item => item.split('(')[item.split('(').length - 1].replace(')', '')) ?? []
            },
            dataToken: dataToken.value
        }))
        const fetchCostPivotDownload = ({ dType }) => {
            loading.value = true
            if (downloadMode.value) {
                const downloadRequestOption = {
                    ...requestOption.value,
                    pivotFilter: {}
                }
                // 수동 프로그레스바 즉시 시작 (비동기 작업용)
                bus.$emit('start:manual-progress')

                store.dispatch('$_costPivot/getCostDownload', downloadRequestOption).then(data => {
                    serverMessage.value = null
                    // runUntilCompletedCostDownload가 프로그레스바를 관리함
                    runUntilCompletedCostDownload(data.dataToken, () => {
                        loading.value = false
                        bus.$emit('end:manual-progress')
                    })
                }).catch(error => {
                    loading.value = false
                    bus.$emit('end:manual-progress')
                    if (error?.response?.data?.message) {
                        serverMessage.value = error.response.data.message
                    }
                })
            } else {
                // 일반 모드에서도 file 타입일 때 수동 프로그레스바 즉시 시작
                if (dType === 'file') {
                    bus.$emit('start:manual-progress')
                }

                store.dispatch('$_costPivot/getCostPivotDownload', {
                    ...requestOption.value,
                    dType
                }).then(data => {
                    serverMessage.value = null
                    if (dType === 'file') {
                        // 프로그레스바는 이미 시작됨, 상태 체크 시작
                        runUntilCompletedDownload(dataToken.value, () => {
                            loading.value = false
                            bus.$emit('end:manual-progress')
                        })
                    } else {
                        loading.value = false
                    }
                }).catch(error => {
                    loading.value = false
                    // 에러 시 프로그레스바 종료
                    if (dType === 'file') {
                        bus.$emit('end:manual-progress')
                    }
                    if (error?.response?.data?.message) {
                        serverMessage.value = error.response.data.message
                    }
                })
            }
        }
        const POLLING_INTERVAL = 7000
        const runUntilCompletedDownload = (dataToken, callback) => {
            let isPolling = true
            const checkStatus = () => {
                if (!isPolling) return
                store.dispatch('$_costPivot/getCostPivotDownloadStatus', { dataToken }).then(data => {
                    if (costPivotDownloadStatus.value === 'DONE' || costPivotDownloadStatus.value === 'EXCEPTION') {
                        if (costPivotDownloadStatus.value === 'EXCEPTION') {
                            serverMessage.value = data.message
                        }
                        isPolling = false
                        callback()
                    } else {
                        setTimeout(() => checkStatus(), POLLING_INTERVAL)
                    }
                })
            }
            setTimeout(() => checkStatus(), POLLING_INTERVAL)
        }
        const runUntilCompletedCostDownload = (dataToken, callback) => {
            let isPolling = true

            const checkStatus = () => {
                if (!isPolling) return

                store.dispatch('$_costPivot/getCostDownloadStatus', { dataToken }).then(data => {
                    if (costDownloadStatus.value === 'DONE' || costDownloadStatus.value === 'EXCEPTION') {
                        if (costDownloadStatus.value === 'EXCEPTION') {
                            serverMessage.value = data.message
                        }
                        isPolling = false
                        callback()
                    } else {
                        setTimeout(() => checkStatus(), POLLING_INTERVAL)
                    }
                })
            }
            // 첫 체크는 10초 후 시작 (API 호출 직후는 아직 처리 중)
            setTimeout(() => checkStatus(), POLLING_INTERVAL)
        }
        const updatePivotFilter = pivotFilterValue => {
            selectedFilterIdx.value = null
            pivotFilter.value = pivotFilterValue
        }
        const fetchPivotDataWithoutParams = () => {
            fetchPivotData({ ...requestOption.value })
        }
        const fetchPivotData = (params) => {
            store.commit(`$_costPivot/${RESET_DOWNLOAD_LINK}`)
            store.commit(`$_costPivot/${RESET_DOWNLOAD_STATUS}`)
            Object.keys(params.pivotFilter).forEach(key => {
                if (!params.compositSources.includes(key)) {
                    delete params.pivotFilter[key]
                    delete pivotFilter.value[key]
                }
            })
            loading.value = true
            return new Promise((resolve, reject) => {
                return store.dispatch('$_costPivot/getCostPivot', params).then(() => {
                    loading.value = false
                    serverMessage.value = null
                    resolve()
                }).catch(error => {
                    loading.value = false
                    if (error.response.status >= 500) {
                        serverMessage.value = `${vm.$t('org.message["Please modify the search criteria and try again."]')}(${error.message || error?.response?.data?.message})`
                    } else {
                        serverMessage.value = vm.$t('org.message["Please modify the search criteria and try again."]')
                    }
                })
            })
        }
        const fetchCostPivotFilter = ({ costPivotFilterIdx }) => {
            selectedFilterIdx.value = costPivotFilterIdx
            store.dispatch('$_costPivot/getCostPivotFilter', { costPivotFilterIdx }).then(() => {
                const rows = costPivotSavedFilter.value.rows
                const cols = costPivotSavedFilter.value.cols
                items.value.compositSources = getCompositSources(rows, cols)
                items.value.rows = costPivotSavedFilter.value.rows
                items.value.cols = costPivotSavedFilter.value.cols
                if (costPivotSavedFilter.value.pivotFilter) {
                    pivotFilter.value = costPivotSavedFilter.value.pivotFilter
                } else if (costPivotSavedFilter.value.valueFilter) {
                    pivotFilter.value = Object.keys(costPivotSavedFilter.value.valueFilter).filter(key => !['usageAccountId', 'group'].includes(key)).map(key => ({
                        key: key,
                        values: Object.keys(costPivotSavedFilter.value.valueFilter[key]).filter(childKey => costPivotSavedFilter.value.valueFilter[key][childKey])
                    })).reduce((acc, item) => {
                        return { ...acc, [item.key]: item.values }
                    }, {})
                }
                fetchPivotDataWithoutParams()
            })
        }
        const updateCompositSources = ({ rows, cols }) => {
            selectedFilterIdx.value = null
            items.value.compositSources = getCompositSources(rows, cols)
            if (!downloadMode.value) {
                fetchPivotDataWithoutParams()
            } else {
                if (costDownloadStatus.value === 'DONE' || costDownloadLink.value) {
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')
                }
            }
        }
        const fetchFilterNames = () => {
            store.dispatch('$_costPivot/getCostPivotFilterNames', { userIdx: userIdx.value })
        }
        const pivotTable = ref(null)
        const exportCsv = () => {
            if (pivotTable.value) {
                pivotTable.value.csvDownload()
            }
        }
        const spacePattern = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g
        const createFilter = ({ name }) => {
            if (spacePattern.test(name) || name.trim() === '') {
                alert(vm.$t('org.message.Remove spaces before and after characters'))
            } else if (typeof name === 'string') {
                const content = {
                    cols: items.value.cols,
                    rows: items.value.rows,
                    pivotFilter: {
                        ...pivotFilter.value,
                        usageAccountId: [],
                        group: []
                    }
                }
                store.dispatch('$_costPivot/createCostPivotFilter', { userIdx: userIdx.value, name, content }).then(() => {
                    fetchFilterNames()
                })
            }
        }
        const deleteFilter = ({ values }) => {
            const deleteCalls = []
            for (let costPivotFilterIdx of values) {
                deleteCalls.push(store.dispatch('$_costPivot/deleteCostPivotFilter', { costPivotFilterIdx }))
            }
            Promise.all(deleteCalls).then(() => fetchFilterNames())
        }
        const filterOptions = computed(() => costPivotFilterNames.value.map(item => ({
            text: item.name,
            value: item.costPivotFilterIdx
        })))
        const handleAddFilter = () => {
            if (filterName.value.trim()) {
                createFilter({ name: filterName.value })
                showAddFilterModal.value = false
                filterName.value = ''
            }
        }
        const handleDeleteFilter = () => {
            if (selectedFiltersToDelete.value.length) {
                deleteFilter({ values: selectedFiltersToDelete.value })
                showDeleteFilterModal.value = false
                selectedFiltersToDelete.value = []
            }
        }
        const changeIndexes = (indexes) => {
            selectedFilterIdx.value = null
            // 다운로드 모드에서는 API 호출하지 않음
            if (!downloadMode.value) {
                fetchPivotData({ ...requestOption.value, indexes })
            } else {
                // 다운로드 모드에서 기간 변경 시 다운로드 상태 초기화
                if (costDownloadStatus.value === 'DONE' || costDownloadLink.value) {
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')
                }
            }
        }
        watch(changeProject, (value, oldValue) => {
            // 프로젝트/그룹 변경 시작
            isProjectChanging.value = true

            // 필터 초기화
            if ((pivotFilter.value.usageAccountId && pivotFilter.value.usageAccountId.length) || (pivotFilter.value.group && pivotFilter.value.group.length)) {
                pivotFilter.value.usageAccountId = []
                pivotFilter.value.group = []
                bus.$emit('request:warning', { title: 'Info', content: vm.$t('org.billing.Filtered values have been deselected in Account or Group field') })
            }

            if (downloadMode.value) {
                // 다운로드 모드에서는 period API 호출하여 조회 가능한 월 목록 가져오기
                loading.value = true

                // 다운로드 상태 초기화
                store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')

                downloadModeReady.value = false
                store.dispatch('$_costPivot/getCostDownloadPeriod', {
                    qtype: qtypeParam.value.qtype,
                    projectIdx: qtypeParam.value.projectIdx,
                    groupIdx: qtypeParam.value.groupIdx
                }).then(response => {
                    // 204 응답인지 확인
                    if (response.status === 204 || !response.summary || Object.keys(response.summary).length === 0) {
                        downloadModeNoData.value = true
                    } else {
                        downloadModeNoData.value = false
                    }
                    downloadModeReady.value = true

                    // period API 응답 처리 - costPivotSummary 업데이트
                    // getCostDownloadPeriod는 { summary: {...} } 형태로 반환
                    const periodData = response.summary
                    if (periodData && periodData.minDate && periodData.maxDate) {
                        store.commit('$_costPivot/UPDATE_COST_PIVOT_SUMMARY', {
                            minDate: periodData.minDate,
                            maxDate: periodData.maxDate,
                            indexes: periodData.indexes,
                            sizeOfData: periodData.sizeOfData
                        })

                        // 선택된 월 범위 초기화 (indexes가 있는 경우)
                        if (periodData.indexes && periodData.indexes.length > 0) {
                            // 최근 3개월 선택 (또는 전체 기간이 3개월 미만이면 전체 선택)
                            const defaultIndexes = periodData.indexes.length <= 3
                                ? periodData.indexes
                                : periodData.indexes.slice(-3)
                            store.commit('$_costPivot/FETCH_SELECTED_MONTH_RANGE', defaultIndexes)
                        } else {
                            // indexes가 없는 경우 빈 배열로 초기화
                            store.commit('$_costPivot/FETCH_SELECTED_MONTH_RANGE', [])
                        }
                    }
                    loading.value = false
                    isProjectChanging.value = false
                }).catch(error => {
                    console.error('Failed to fetch download period:', error)
                    loading.value = false
                    isProjectChanging.value = false
                })
            } else {
                // 일반 모드: 원래 로직대로 changeIndexes([]) 호출
                // changeIndexes가 빈 배열로 fetchPivotData를 호출하고,
                // 서버가 자동으로 최근 3개월 데이터와 함께 summary를 반환
                // summary의 indexes는 getCostPivot action에서 자동으로 store에 업데이트됨
                isProjectChanging.value = false // 플래그 즉시 해제하여 중복 호출 방지
                changeIndexes([])
            }
        }, {
            deep: true
        })
        watch(selectedMonthRange, (value, oldValue) => {
            if (!value.length || !oldValue.length) return
            // 모드 전환 중이거나 프로젝트 변경 중에는 중복 호출 방지
            if (isModeSwitching.value || isProjectChanging.value) {
                return
            }
            changeIndexes(value)
        }, {
            deep: true
        })
        watch(costPivotFilterContentValue, (value, oldValue) => {
            items.value.value = value
            // 다운로드 모드에서는 API 호출하지 않음
            if (!downloadMode.value) {
                fetchPivotDataWithoutParams()
            } else {
                // 다운로드 모드에서 Value 변경 시 다운로드 상태 초기화
                if (costDownloadStatus.value === 'DONE' || costDownloadLink.value) {
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_STATUS')
                    store.commit('$_costPivot/RESET_COST_DOWNLOAD_LINK')
                }
            }
        })
        return {
            selectedMonthRange,
            internalUser,
            statusCode,
            dataToken,
            serverMessage,
            theme,
            themeColors,
            pivotFilter,
            items,
            selectedFilterIdx,
            loading,
            accountMap,
            costPivotData,
            costPivotSummary,
            costPivotFilterNames,
            costPivotFilterContent,
            costPivotDownloadLink,
            costPivotDownloadStatus,
            pivotTable,
            fieldStatus,
            downloadMode,
            downloadModeNoData,
            downloadModeReady,
            onDownloadModeChange,
            costDownloadLink,
            costDownloadStatus,
            showAddFilterModal,
            showDeleteFilterModal,
            filterName,
            selectedFiltersToDelete,
            filterOptions,
            handleAddFilter,
            handleDeleteFilter,
            changeIndexes,
            updateCompositSources,
            syncFilter,
            exportCsv,
            createFilter,
            deleteFilter,
            fetchCostPivotFilter,
            fetchPivotData,
            fetchFilterNames,
            fetchCostPivotDownload,
            updatePivotFilter,
            fetchPivotDataWithoutParams
        }
    }
}
</script>

<style>
/* 부트스트랩 컬럼 전환 애니메이션 제거 */
.col-md-3, .col-md-4, .col-md-7 {
    transition: none !important;
}

/* 다운로드 모드 스위치 오른쪽 마진 제거 */
.custom-control-inline.mr-0 {
    margin-right: 0 !important;
}

</style>
